'use client'

import React, { ComponentType, useState } from 'react'
import { Round, Schedule, Tournament, TournamentRoundSchedule } from 'types'
import { roundPath } from 'shared/urlHelpers'
import { formattedDate } from 'shared/timeHelper'
import { ArrowLeft, ArrowRight, ChevronDown } from 'react-feather'
import Link from 'components/router/Link'
import GameSchedule from 'components/schedule/GameSchedule'
import Button from 'components/ui/basic/Button'
import LinkButton from 'components/router/LinkButton'
import { SmoothTransition } from 'components/ui/SmoothTransition'

export const GameDayPrevButton: ComponentType<{
  schedule: TournamentRoundSchedule
  className?: string
}> = ({ schedule, className = '' }) => {
  if (!schedule.tournament?.rounds) {
    return null
  }
  const round = schedule.tournament.rounds.find(
    (round) => schedule.round.id === round.id
  )
  if (!round) {
    return null
  }
  const position = schedule.tournament.rounds.indexOf(round)
  const prevRound = schedule.tournament.rounds[position - 1]

  if (prevRound) {
    return (
      <LinkButton
        href={roundPath(schedule.tournament, prevRound)}
        className={`inline-flex ${className}`}
        buttonClassName="p-0"
        iconOnly
        color="transparent"
      >
        <ArrowLeft className="mr-1" />
        Vorheriger Spieltag
      </LinkButton>
    )
  } else {
    return (
      <Button
        className={`opacity-50 p-0 inline-flex ${className}`}
        disabled
        iconOnly
        color="transparent"
      >
        <ArrowLeft className="mr-1" />
        Vorheriger Spieltag
      </Button>
    )
  }
}
export const GameDayNextButton: ComponentType<{
  schedule: TournamentRoundSchedule
  className?: string
}> = ({ schedule, className = '' }) => {
  if (!schedule.tournament?.rounds) {
    return null
  }
  const round = schedule.tournament.rounds.find(
    (round) => schedule.round.id === round.id
  )
  if (!round) {
    return null
  }
  const position = schedule.tournament.rounds.indexOf(round)
  const nextRound = schedule.tournament.rounds[position + 1]

  if (nextRound) {
    return (
      <LinkButton
        href={roundPath(schedule.tournament, nextRound)}
        className={`inline-flex ${className}`}
        buttonClassName="p-0"
        iconOnly
        color="transparent"
      >
        Nächster Spieltag
        <ArrowRight className="ml-1" />
      </LinkButton>
    )
  } else {
    return (
      <Button
        className={`opacity-50 p-0 inline-flex ${className}`}
        disabled
        iconOnly
        color="transparent"
      >
        Nächster Spieltag
        <ArrowRight className="ml-1" />
      </Button>
    )
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
const GameDayOverlay = ({
  schedule
}: {
  schedule: TournamentRoundSchedule
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const activeRound = (round: Round) => round.id === schedule.round.id

  return (
    // Has to be inside div, otherwise button jumps on Transition show/hide
    <div>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        color="transparent"
        className="p-0 textXl"
      >
        {schedule.round.name}
        <ChevronDown className="text-primary ml-1" />
      </Button>
      <SmoothTransition show={isOpen}>
        <div className="overlay">
          <div className="overlay-wrapper">
            <div className="overlay-backdrop-wrapper" aria-hidden="true">
              <div
                className="overlay-backdrop"
                onClick={() => setIsOpen(!isOpen)}
              ></div>
            </div>

            <div
              className="overlay-content-wrapper bg-gray-200"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <p className="headline mb-4 flex-1">Spieltag wählen</p>
              <ul className="overflow-y-auto mb-4 border border-gray-900 bg-white rounded p-4 h-full">
                {schedule.tournament.rounds?.map((round: Round) => (
                  <li key={round.id} className="mb-4">
                    <Link
                      href={roundPath(schedule.tournament, round)}
                      onClick={() => setIsOpen(!isOpen)}
                      className={`flex justify-between items-center ${
                        activeRound(round) ? 'text-primary' : ''
                      }`}
                    >
                      <span>{round.name}</span>
                      <span
                        className={`text-gray-500 text-sm whitespace-nowrap pl-2${
                          activeRound(round) ? ' text-primary' : ''
                        }`}
                      >
                        (
                        {round.startsAt &&
                          round.endsAt &&
                          `${formattedDate(round.startsAt)} - ${formattedDate(
                            round.endsAt
                          )}`}
                        )
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="flex flex-1">
                <Button onClick={() => setIsOpen(!isOpen)} className="w-full">
                  Schliessen
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SmoothTransition>
    </div>
  )
}

const RoundSchedule: ComponentType<{
  schedule: Schedule
  tournament: Tournament
}> = ({ schedule, tournament }) => {
  return (
    <>
      <GameSchedule
        schedule={schedule}
        tournament={tournament}
        // TODO disabled until we have proper data
        // navigation={
        //   <div className="gameday-nav bg-blue-dark md:bg-transparent text-white md:text-bodyColor py-1 px-6 md:px-8 mt-0 mb-3 container-offset flex justify-between md:justify-start items-center">
        //     <div className="view-horizontal justify-between w-full md:w-auto">
        //       <GameDayOverlay schedule={schedule} />
        //     </div>
        //   </div>
        // }
      />
    </>
  )
}

export default RoundSchedule
